import Vue from 'vue';
import localForage from 'localforage';

const storage = localForage.createInstance({
    name: 'orderLayout',
});

function getDefaultStoreName() {
    return window.location.pathname;
}

const mapPagesToServerObject = function(orderLayout) {
    const serverObject = _.pick(orderLayout, ['name', 'width', 'height']);
    let pages = orderLayout.pages;

    // set page sequence
    _.forEach(_.groupBy(pages, ['group_id']), groupPages => {
        _.sortBy(groupPages, p => {
            // push to last array if zero
            if (p.sequence == 0) return 999;
            return p.sequence;
        }).forEach((p, index) => {
            p.sequence = index + 1;
        });
    });

    serverObject.pages = pages.map(p => {
        const pickAttr = ['name', 'sequence', 'group_id'];
        if (p.id > 0) pickAttr.push('id');

        const page = _.pick(p, pickAttr);

        if (p.cells.length) {
            page.cells = p.cells
                .filter(c => !_.isNil(c.action_id))
                // remove cell overflow due to grid resize
                .filter(
                    c => c.x < orderLayout.width && c.y < orderLayout.height,
                );
        }

        return page;
    });

    serverObject.centrals = orderLayout.centrals.map(c => c.id);

    return serverObject;
};

export default {
    namespaced: true,
    state: {
        activePage: null,
        activeGroup: null,
        orderLayout: {
            id: null,
            width: 4,
            height: 5,
            pages: [],
            centrals: [],
        },
        groupList: [],
        groupDisplay: [],
        tempPageId: -1, // use to store temporary unique id
        previousPages: [],
        selectedGrid: null,
        excludeCentrals: [],
        totalRecords: 0,
        medias: [],
    },

    getters: {
        orderLayout: state => state.orderLayout,
        centrals: state => state.orderLayout.centrals,
        excludeCentrals: state => state.excludeCentrals,
        getPageCellByGrid: state => (page_id, x, y, return_empty = false) => {
            const page = state.orderLayout.pages.find(p => p.id == page_id);
            const cell = page.cells.find(c => c.x == x && c.y == y);
            const emptyCell = {
                x,
                y,
                width: 1,
                height: 1,
                color: '#666666',
                action_type: null,
                action_id: null,
            };

            return cell || (return_empty ? emptyCell : null);
        },
        getCellIndexByGrid: (state, getters) => (page_id, x, y) => {
            const pageIndex = getters.getPageIndexById(page_id);
            return state.orderLayout.pages[pageIndex].cells.findIndex(
                c => c.x == x && c.y == y,
            );
        },
        getPageById: state => page_id => {
            return state.orderLayout.pages.find(p => p.id == page_id);
        },
        getPageIndexById: state => page_id => {
            return state.orderLayout.pages.findIndex(p => p.id == page_id);
        },
        getGroupById: state => groupId => {
            return state.groupList.find(g => g.id == groupId) || null;
        },
        getGroupPages: state => groupId =>
            state.orderLayout.pages.filter(p => p.group_id == groupId),
        activeGroupPages: (state, getters) => {
            const pages = getters.getGroupPages(getters.activeGroupId);
            return _.sortBy(pages, p => {
                if (p.sequence == 0) return 999;
                return p.sequence;
            });
        },
        pages: state => state.orderLayout.pages,
        hasActivePage: state => state.activePage !== null,
        activePage: state => state.activePage,
        activeGroup: state => state.activeGroup,
        activeGroupId: state =>
            state.activeGroup !== null ? state.activeGroup.id : null,
        hasActiveGroup: state => state.activeGroup !== null,
        groupList: state => state.groupList,
        groupDisplay: state => state.groupDisplay,
        previousPages: state => state.previousPages,
        hasPreviousPages: state => state.previousPages.length,
        selectedGrid: state => state.selectedGrid,
        hasSelectedGrid: state => state.selectedGrid !== null,
        getNextEmptyGrid: (state, getters) => (page_id, x, y) => {
            for (let i = y; i < getters.orderLayout.height; i++) {
                // find x sibling if any
                let xGrid = i == y ? x + 1 : 0;
                for (let j = xGrid; j < getters.orderLayout.width; j++) {
                    const cell = getters.getPageCellByGrid(page_id, j, i, true);
                    if (_.isNull(cell.action_type))
                        return { page_id, x: j, y: i };
                }
            }

            return undefined;
        },
        getNextPage: (state, getters) => (page_id, groupId = null) => {
            const pages = getters.getGroupPages(groupId);
            const nextIndex = _.findIndex(pages, p => p.id == page_id) + 1;

            if (page_id === _.last(pages).id) return undefined;
            return pages[nextIndex];
        },
        getNextPageWithEmptyGrid: (state, getters) => (
            page_id,
            groupId,
            x = 0,
            y = 0,
        ) => {
            const pages = getters.getGroupPages(groupId);
            let pageIndex = _.findIndex(pages, p => p.id == page_id);
            const nextPages = pages.filter((p, index) => index > pageIndex);

            if (!nextPages.length) return undefined;

            for (let i = 0; i < nextPages.length; i++) {
                pageIndex++;
                const nextGrid = getters.getNextEmptyGrid(
                    pages[pageIndex].id,
                    x,
                    y,
                );
                if (!_.isNil(nextGrid)) return nextGrid;
            }

            return undefined;
        },

        totalRecords: state => state.totalRecords,

        getCellPendingMedia: state => cell => {
            return state.medias.filter(m => {
                const d = m.data;

                if (cell.id && cell.id > 0) {
                    return d.cell_id === cell.id;
                }

                return (
                    d.page_id === cell.page_id &&
                    d.x === cell.x &&
                    d.y === cell.y
                );
            });
        },

        allRawPendingMedia: state => state.medias.map(m => m),
    },

    mutations: {
        setOrderLayout(state, orderLayout) {
            state.orderLayout = orderLayout;
        },

        setOrderLayoutAttribute(state, { key, value }) {
            state.orderLayout[key] = value;
        },

        setPageAttribute(state, { pageIndex, key, value }) {
            const page = state.orderLayout.pages[pageIndex];
            Vue.set(page, key, value);
        },

        setPageCell(state, { pageIndex, cellIndex, attributes }) {
            const cells = state.orderLayout.pages[pageIndex].cells;

            Vue.set(
                cells,
                cellIndex,
                Object.assign({}, cells[cellIndex], attributes),
            );
        },

        addPage(state, page) {
            state.orderLayout.pages.push(page);
        },

        removePage(state, pageIndex) {
            state.orderLayout.pages.splice(pageIndex, 1);
        },

        updatePage(state, { pageIndex, key, value }) {
            state.orderLayout.pages[pageIndex][key] = value;
        },

        addPageCell(state, { pageIndex, attributes }) {
            const cells = state.orderLayout.pages[pageIndex].cells;
            cells.push(attributes);
        },

        setActivePage(state, page) {
            state.activePage = page;
        },

        setActiveGroup(state, group) {
            state.activeGroup = group;
        },

        removePageCell(state, { pageIndex, cellIndex }) {
            state.orderLayout.pages[pageIndex].cells.splice(cellIndex, 1);
        },

        addGroup(state, group) {
            state.groupList.push(group);
        },

        addPreviousPage(state, page) {
            state.previousPages.push(page);
        },

        removeLastPreviousPage(state) {
            state.previousPages.splice(state.previousPages.length - 1, 1);
        },

        removePreviousPages(state, { index, total = undefined }) {
            if (_.isNil(total)) state.previousPages.splice(index);
            else state.previousPages.splice(index, total);
        },

        setSelectedGrid(state, grid) {
            state.selectedGrid = grid;
        },

        setGroupList(state, groupList) {
            state.groupList = groupList;
        },

        setGroupDisplayList(state, groupList) {
            state.groupDisplay = groupList;
        },

        removeGroup(state, groupIndex) {
            state.groupList.splice(groupIndex, 1);
        },

        setCentrals(state, centrals) {
            state.orderLayout.centrals = centrals;
        },

        setTempPageId(state, id) {
            state.tempPageId = id;
        },

        setTotalRecords(state, total) {
            state.totalRecords = total;
        },

        addCellPendingMedia(state, pendingMedia) {
            state.medias.push(pendingMedia);
        },

        clearPendingMedia(state) {
            state.medias = [];
        },

        removeCellPendingMedia(state, cell) {
            const pm = state.medias.findIndex(m => {
                const d = m.data;

                if (cell.id && cell.id > 0) {
                    return d.cell_id === cell.id;
                }

                return (
                    d.page_id === cell.page_id &&
                    d.x === cell.x &&
                    d.y === cell.y
                );
            });

            if (pm > -1) {
                state.medias.splice(pm, 1);
            }
        },

        updateMedia(state, medias) {
            state.medias = medias;
        },
    },

    actions: {
        setOrCreatePageCell(
            { commit, getters, dispatch },
            {
                page_id,
                x,
                y,
                width = 1,
                height = 1,
                color = '#666666',
                action_type = null,
                action_id = null,
                action = null,
                remove_media_id = null,
            } = {},
        ) {
            const pageIndex = getters.getPageIndexById(page_id);
            const cellIndex = getters.getCellIndexByGrid(page_id, x, y);

            const params = {
                pageIndex,
                cellIndex,
                attributes: {
                    page_id,
                    x,
                    y,
                    width,
                    height,
                    color,
                    action_type,
                    action_id,
                    action,
                    remove_media_id,
                },
            };

            if (cellIndex < 0) commit('addPageCell', params);

            commit('setPageCell', params);
            dispatch('saveLocalOrderLayout');
        },

        setActivePage({ commit }, page) {
            commit('setActivePage', page);
        },

        removePageCell({ commit, getters, dispatch }, { page_id, x, y }) {
            const pageIndex = getters.getPageIndexById(page_id);
            const cellIndex = getters.getCellIndexByGrid(page_id, x, y);

            commit('removePageCell', { pageIndex, cellIndex });
            dispatch('saveLocalOrderLayout');
        },

        addPage(
            { commit, getters, state, dispatch },
            { name = null, group_id = null, cells = [] } = {},
        ) {
            if (name === null) {
                const pageNo =
                    getters.orderLayout.pages.filter(
                        p => p.group_id == group_id,
                    ).length + 1;

                name = `Tab ${pageNo}`;
            }

            const id = state.tempPageId;
            state.tempPageId--;

            const page = {
                id,
                name,
                group_id,
                cells,
            };

            commit('addPage', page);
            dispatch('saveLocalOrderLayout');

            return page;
        },

        removePage({ commit, getters, dispatch }, page_id) {
            const pageIndex = getters.getPageIndexById(page_id);
            commit('removePage', pageIndex);
            dispatch('saveLocalOrderLayout');
        },

        updatePage({ commit, getters, dispatch }, { page_id, key, value }) {
            const pageIndex = getters.getPageIndexById(page_id);
            commit('updatePage', { pageIndex, key, value });
            dispatch('saveLocalOrderLayout');
        },

        viewGroup({ commit, getters, dispatch }, groupId) {
            const groupPages = getters.getGroupPages(groupId);

            // back to parent if the selected group is a parent
            if (getters.hasPreviousPages) {
                const page = getters.previousPages.find(
                    p => p.group_id == groupId,
                );

                if (!_.isNil(page)) {
                    const index = _.findIndex(
                        getters.previousPages,
                        p => p.group_id == groupId,
                    );

                    commit('removePreviousPages', { index });
                    dispatch('viewPage', page);
                    return;
                }
            }

            commit('addPreviousPage', getters.activePage);
            dispatch('viewPage', _.first(groupPages));
        },

        viewPage({ commit, getters }, page) {
            commit('setActivePage', page);
            commit('setActiveGroup', getters.getGroupById(page.group_id));
        },

        backToPreviousPage({ dispatch, commit, getters }) {
            dispatch('viewPage', _.last(getters.previousPages));
            commit('removeLastPreviousPage');
        },

        setSelectedGrid(
            { commit },
            { page_id = null, x = null, y = null } = {},
        ) {
            const grid =
                page_id === null && x === null && y === null
                    ? null
                    : { page_id, x, y };
            commit('setSelectedGrid', grid);

            return grid;
        },

        async getOrderLayout({ commit }, id) {
            const { data } = await axios.get(
                Laroute.route('franchise.order_layout.show', { id }),
            );

            commit('setOrderLayout', data);

            return data;
        },

        createOrderLayout({ commit }, payload) {
            return axios.post(
                Laroute.route('franchise.order_layout.create'),
                payload,
            );
        },

        async updateOrderLayout({ commit, dispatch, getters }, orderLayout) {
            const store = getDefaultStoreName();

            const url = Laroute.route('franchise.order_layout.update', {
                id: orderLayout.id,
            });

            try {
                const { data } = await axios.put(
                    url,
                    mapPagesToServerObject(orderLayout),
                );

                commit('setOrderLayout', data);
                dispatch('resetLocalOrderLayout', store);

                if (getters.activePage.id < 1) {
                    commit('setActiveGroup', null);
                    dispatch('viewPage', _.first(getters.activeGroupPages));
                }

                return data;
            } catch (e) {
                throw e;
            }
        },

        async getDisplayGroupList({ commit }, params = {}) {
            let options = {
                params: _.merge(
                    {
                        limit: 10,
                        query: null,
                    },
                    params,
                ),
            };
            const { data } = await axios.get(
                Laroute.route('franchise.order_layout.group.index'),
                options,
            );

            commit('setGroupDisplayList', data.data);
            commit('setTotalRecords', data.count);
            return data;
        },

        async getGroupList({ commit }) {
            const { data } = await axios.get(
                Laroute.route('franchise.order_layout.group.index'),
            );

            commit('setGroupList', data.data);
            return data;
        },

        async createGroup({ commit }, group) {
            const { data } = await axios.post(
                Laroute.route('franchise.order_layout.group.create'),
                group,
            );

            commit('addGroup', data);
            return data;
        },

        async updateGroup({ commit, getters, dispatch }, group) {
            const { data } = await axios.put(
                Laroute.route('franchise.order_layout.group.update', {
                    id: group.id,
                }),
                group,
            );

            const groupIndex = getters.groupList.findIndex(
                g => g.id == group.id,
            );

            if (groupIndex >= 0) dispatch('removeGroup', group.id);
            commit('addGroup', data);
            return data;
        },

        async deleteGroup({ commit, getters }, id) {
            const url = Laroute.route('franchise.order_layout.group.delete', {
                id,
            });

            try {
                await axios.delete(url);
                return id;
            } catch (e) {
                throw e;
            }
        },

        setCentrals({ commit, dispatch }, centrals) {
            commit('setCentrals', centrals);
            dispatch('saveLocalOrderLayout');
        },

        setExcludeCentrals({ state }, centralIds) {
            state.excludeCentrals = centralIds;
        },

        setOrderLayoutAttribute({ commit, dispatch }, keyValue) {
            commit('setOrderLayoutAttribute', keyValue);
            dispatch('saveLocalOrderLayout');
        },

        setPageAttribute(
            { commit, getters, dispatch },
            { page_id, key, value },
        ) {
            const pageIndex = getters.getPageIndexById(page_id);
            commit('setPageAttribute', { pageIndex, key, value });
            dispatch('saveLocalOrderLayout');
        },

        removeGroup({ commit, getters }, id) {
            const groupIndex = getters.groupList.findIndex(g => g.id == id);
            commit('removeGroup', groupIndex);
        },

        async cloneOrderLayout(context, orderLayout) {
            const url = Laroute.route('franchise.order_layout.clone', {
                id: orderLayout.id,
            });

            try {
                const { data } = await axios.patch(url, orderLayout);
                return data;
            } catch (e) {
                throw e;
            }
        },

        getLocalOrderLayout({ commit, dispatch }, store = null) {
            if (store === null) {
                store = getDefaultStoreName();
            }

            return storage.getItem(store).then(orderLayout => {
                if (orderLayout !== null) {
                    commit('setOrderLayout', orderLayout);
                    dispatch('setTempPageIdWithMinValue');
                }

                return orderLayout;
            });
        },

        resetLocalOrderLayout(context, store = null) {
            if (store === null) {
                store = getDefaultStoreName();
            }

            return storage.removeItem(store);
        },

        saveLocalOrderLayout({ state }, store = null) {
            if (store === null) {
                store = getDefaultStoreName();
            }

            return storage.setItem(store, state.orderLayout);
        },

        setTempPageIdWithMinValue({ state, commit }) {
            const pageIds = state.orderLayout.pages.map(p => p.id);
            if (!pageIds.length) return;

            const minId = _.min(pageIds);

            // if has temp page id
            if (minId < 0) {
                commit('setTempPageId', minId - 1);
            }
        },
    },
};



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/order-layout.js